import React, { memo, useState, useEffect } from 'react';
import styled from '@emotion/styled';

import Portal from '@nykaa/nes-ui-components/Portal';
import { BODY_OVERFLOW_FOR_MODAL } from 'shared/constants/dom';
import { css, keyframes } from '@emotion/core';

interface ModalContentProps {
  background: string;
  openAnimation: boolean;
  isAppParityFilter?: boolean;
}

const slideIn = keyframes`
  from {
    margin-bottom: -100%;
  }
  to {
    margin-bottom: 0;
  }
`;
const ModalContent = styled.div`
  position: fixed;
  max-width: 100%;
  background: ${({ background }: ModalContentProps) => background};
  border-radius: 2px;
  z-index: 10;
  border: ${({ isAppParityFilter }) =>
    !isAppParityFilter ? '1px solid rgba(0, 0, 0, 0.2)' : 'none'};
  box-shadow: 0 0 30px 6px rgba(0, 0, 0, 0.15);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: scroll;

  &.partial-modal {
    top: 50%;
    left: 50%;
    width: 100%;
    transform: translate(-50%, -50%);
    height: auto;
    max-width: 500px;
    overflow: hidden;
    max-width: 500px;
  }
  &.stick-to-bottom {
    top: unset;
    bottom: 0;
    margin: auto;
    max-width: 500px;
    transform: translateX(-50%);
    overflow: hidden;
  }
  &.bottom-drawer {
    top: unset;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60vh;
    background: #fff;
    border: none;
    border-radius: 14px 14px 0 0;
    overflow: hidden;
  }
  ${({ openAnimation }: ModalContentProps) =>
    openAnimation &&
    css`
      animation: ${slideIn} 0.6s ease-in-out;
    `}
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9;

  .hide-overlay {
    z-index: 1;
    background-color: rgba(0, 0, 0, 0);
  }
`;

interface ModalProps {
  children: React.ReactNode;
  onHide: VoidFunction;
  modalContentClass?: string;
  allowBackdropClick?: boolean;
  modalBackground?: string;
  isVisible?: boolean;
  isPartial?: boolean;
  isStickBottom?: boolean;
  isBottomDrawer?: boolean;
  contentStyle?: { [key: string]: string };
  modalRef?: React.RefObject<HTMLDivElement>;
  openAnimation?: boolean;
  isAppParityFilter?: boolean;
}

const Modal = ({
  children,
  onHide,
  allowBackdropClick = true,
  modalContentClass = '',
  modalBackground = '#fff',
  isVisible,
  isPartial,
  isStickBottom,
  contentStyle,
  isBottomDrawer,
  modalRef,
  openAnimation = false,
  isAppParityFilter = false,
}: ModalProps) => {
  const [isHidden, setIsHidden] = useState(isVisible);

  useEffect(() => {
    document.body.classList.add(BODY_OVERFLOW_FOR_MODAL);
    return () => document.body.classList.remove(BODY_OVERFLOW_FOR_MODAL);
  }, []);

  const hide = () => {
    setIsHidden(true);
    onHide();
  };
  // TODO: Quicker fix is done but need to update this (not able to pass custom class here
  let modalCustomClass = `${modalContentClass} ${
    isStickBottom ? 'stick-to-bottom' : ''
  } ${isPartial ? 'partial-modal' : ''} `;
  modalCustomClass = isBottomDrawer ? 'bottom-drawer' : modalCustomClass;
  const overlayClass = isHidden ? '' : 'hide-overlay';
  const backdropClick = allowBackdropClick ? hide : () => {};

  return (
    <Portal>
      <ModalContent
        className={modalCustomClass}
        background={modalBackground}
        style={contentStyle}
        ref={modalRef ? modalRef : null}
        openAnimation={openAnimation}
        isAppParityFilter={isAppParityFilter}
      >
        {children}
      </ModalContent>
      <Overlay
        className={overlayClass}
        onClick={backdropClick}
      />
    </Portal>
  );
};

export default memo(Modal);
